/*
 * Copyright (c) 2026. BrightNight. All rights reserved.
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential.
 */

import type { AgentPageContext } from './types';

export interface AgentPageContextLocation {
  pathname: string;
  search: string;
}

const firstSearchValue = (
  searchParams: URLSearchParams,
  ...keys: Array<string>
): string | undefined => {
  for (const key of keys) {
    const value = searchParams.get(key)?.trim();
    if (value) return value;
  }
  return undefined;
};

const allSearchValues = (searchParams: URLSearchParams, key: string): Array<string> =>
  searchParams
    .getAll(key)
    .map((value) => value.trim())
    .filter(Boolean);

const commaSeparatedSearchValues = (searchParams: URLSearchParams, key: string): Array<string> =>
  allSearchValues(searchParams, key).flatMap((value) =>
    value
      .split(',')
      .map((part) => part.trim())
      .filter(Boolean),
  );

const routeProjectId = (pathname: string): string | undefined => {
  const match = pathname.match(/^\/projects\/([^/]+)/);
  return match?.[1];
};

const routeBidRequestId = (pathname: string): string | undefined => {
  const match = pathname.match(/^\/bid-requests\/([^/?#]+)/);
  if (!match || ['new', 'pricing-dashboard'].includes(match[1])) return undefined;
  return match[1];
};

const routeScenarioId = (pathname: string): string | undefined => {
  const match = pathname.match(/^\/projects\/[^/]+\/results\/([^/?#]+)/);
  if (!match || match[1] === 'compare') return undefined;
  return match[1];
};

const bidRequestContext = (
  pathname: string,
  searchParams: URLSearchParams,
): Pick<AgentPageContext, 'bid_request_id' | 'bid_request_version'> => {
  const bidRequestId = routeBidRequestId(pathname);
  if (!bidRequestId) return {};
  const bidRequestVersion = firstSearchValue(searchParams, 'version');
  return {
    bid_request_id: bidRequestId,
    ...(bidRequestVersion ? { bid_request_version: bidRequestVersion } : {}),
  };
};

const configContext = (
  searchParams: URLSearchParams,
  isBidRequestRoute: boolean,
): Pick<AgentPageContext, 'config_id' | 'config_version'> => {
  const configId = firstSearchValue(searchParams, 'config', 'config_id');
  const configVersion =
    firstSearchValue(searchParams, 'config_version') ??
    (isBidRequestRoute ? undefined : firstSearchValue(searchParams, 'version'));
  return {
    ...(configId ? { config_id: configId } : {}),
    ...(configVersion ? { config_version: configVersion } : {}),
  };
};

const routeWorkflowId = (pathname: string, searchParams: URLSearchParams): string | undefined => {
  const netBenefitMatch = pathname.match(/^\/plant(?:-v2)?\/net-benefit\/([^/?#]+)/);
  if (netBenefitMatch) {
    // The simulate route carries the workflow it is editing in the query string instead.
    return netBenefitMatch[1] === 'simulate'
      ? firstSearchValue(searchParams, 'workflow_id', 'id')
      : netBenefitMatch[1];
  }

  const plantBatchMatch = pathname.match(/^\/plant\/batch\/([^/?#]+)/);
  if (plantBatchMatch && !['results', 'simulate'].includes(plantBatchMatch[1])) {
    return plantBatchMatch[1];
  }

  if (pathname === '/plant/batch/simulate' || pathname === '/plant-v2/batch/simulate') {
    return firstSearchValue(searchParams, 'workflow_id', 'id');
  }

  const projectBatchMatch = pathname.match(/^\/projects\/[^/]+\/batch\/([^/?#]+)/);
  if (projectBatchMatch && !['merged-results', 'simulate'].includes(projectBatchMatch[1])) {
    return projectBatchMatch[1];
  }

  if (/^\/projects\/[^/]+\/batch\/simulate$/.test(pathname)) {
    return firstSearchValue(searchParams, 'workflow_id', 'id');
  }

  return firstSearchValue(searchParams, 'workflow_id');
};

export const getAgentPageContext = (location: AgentPageContextLocation): AgentPageContext => {
  const searchParams = new URLSearchParams(location.search);
  const scenarioIds = [
    ...allSearchValues(searchParams, 'scenario'),
    ...allSearchValues(searchParams, 'scenario_id'),
  ];
  const runIds = allSearchValues(searchParams, 'run_id');
  const workflowIds = commaSeparatedSearchValues(searchParams, 'ids');
  const brwfContext = bidRequestContext(location.pathname, searchParams);
  const pageContext: AgentPageContext = {
    route: `${location.pathname}${location.search}`,
    ...brwfContext,
    ...configContext(searchParams, Boolean(brwfContext.bid_request_id)),
  };

  const projectId = routeProjectId(location.pathname);
  if (projectId) pageContext.project_id = projectId;

  const routeScenario = routeScenarioId(location.pathname);
  if (routeScenario) pageContext.scenario_id = routeScenario;

  const workflowId = routeWorkflowId(location.pathname, searchParams);
  if (workflowId) pageContext.workflow_id = workflowId;

  if (scenarioIds.length === 1) {
    pageContext.scenario_id = scenarioIds[0];
  } else if (scenarioIds.length > 1) {
    pageContext.comparison_scenario_ids = scenarioIds;
  }

  if (runIds.length === 1) {
    pageContext.run_id = runIds[0];
  } else if (runIds.length > 1) {
    pageContext.comparison_run_ids = runIds;
  }

  if (workflowIds.length === 1) {
    pageContext.workflow_id = workflowIds[0];
  } else if (workflowIds.length > 1) {
    pageContext.comparison_workflow_ids = workflowIds;
  }

  return pageContext;
};
